import { useCallback, useState } from 'react';
import { commitUnitOrderImport, previewUnitOrderImport } from 'services/unit-orders';
import { UnitOrderImportResult, UnitOrderImportRowResult } from 'types';

export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
export const MAX_IMPORT_ROWS = 5000;

/* Rebuilds a CSV from retained failed rows so they can be retried as a new import. */
const buildCsv = (rows: UnitOrderImportRowResult[]): string => {
  const escape = (value?: string) => `"${(value ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map(row =>
    [row.account, row.unitNumber, row.notes ?? '', row.requestedAction].map(escape).join(',')
  );
  return ['Account,UnitNumber,Notes,Hold', ...lines].join('\n');
};

export const useOrderImport = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<UnitOrderImportResult | null>(null);
  const [result, setResult] = useState<UnitOrderImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  const runPreview = useCallback(async (selected: File): Promise<UnitOrderImportResult | null> => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await previewUnitOrderImport(selected);
      setPreview(response);
      return response;
    } catch (e: any) {
      setError(e?.message ?? 'Preview failed');
      setPreview(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const selectFile = useCallback(
    async (selected: File | null): Promise<UnitOrderImportResult | null> => {
      setFile(selected);
      setPreview(null);
      setResult(null);
      setError(null);
      if (!selected) return null;
      if (selected.size > MAX_IMPORT_BYTES) {
        setError('File exceeds the 10 MB limit.');
        return null;
      }
      return await runPreview(selected);
    },
    [runPreview]
  );

  const commit = useCallback(async (): Promise<UnitOrderImportResult | null> => {
    if (!file) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await commitUnitOrderImport(file);
      setResult(response);
      setPreview(null);
      return response;
    } catch (e: any) {
      setError(e?.message ?? 'Import failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [file]);

  const retryFailed = useCallback(async (): Promise<UnitOrderImportResult | null> => {
    const failed = result?.rows.filter(row => row.outcome === 'FAILED') ?? [];
    if (failed.length === 0) return null;
    const retryFile = new File([buildCsv(failed)], 'unit-orders-retry.csv', { type: 'text/csv' });
    setFile(retryFile);
    return await runPreview(retryFile);
  }, [result, runPreview]);

  return { file, preview, result, loading, error, selectFile, reset, commit, retryFailed };
};
